import { computed, type ComputedRef, type Ref } from "vue";

/**
 * Generic, store-free engine for an ordered sub-list ("slice") that lives inside a larger
 * parent array. Fully decoupled from the highlights domain: it only requires that items have
 * a string `id`, and it never touches the parent array directly — the caller injects both
 * how to READ its slice out of the parent (`select`) and how to WRITE a new slice back into
 * it (`commit`). Ordering is the array index within the slice.
 *
 * This is the shared base for `usePremadeList` and `useCustomList`: both slices of the same
 * `HighlightItem[]` need identical remove / move / reorder mechanics, differing only in which
 * items belong to them and how a rebuilt slice is merged back (premade-before-custom). Those
 * two rules are the `select` / `commit` parameters; everything mechanical lives here.
 *
 * @typeParam TParent - the element type of the parent array.
 * @typeParam T - the (narrowed) element type of this slice; must carry a string `id`.
 *
 * @param parent - the writable parent array ref (e.g. the host's `defineModel`).
 * @param select - projects the parent array to this slice, in order.
 * @param commit - rebuilds the parent array with this slice replaced (leaving others intact).
 */
export function useIndexedList<TParent, T extends { id: string }>(
  parent: Ref<TParent[]>,
  select: (parent: TParent[]) => T[],
  commit: (nextSlice: T[]) => TParent[]
) {
  /** This slice, in order. Order source of truth = array index. */
  const items: ComputedRef<T[]> = computed(() => select(parent.value));

  /** Removes the item with the given id. No-op for an unknown id. */
  function remove(id: string): void {
    const next = items.value.filter((item) => item.id !== id);
    if (next.length === items.value.length) {
      return;
    }
    parent.value = commit(next);
  }

  /**
   * Moves an item by `delta` positions within the slice (negative = up). Clamped at the
   * slice boundaries — a delta that would run past an edge is a no-op — so it never crosses
   * out of the slice.
   */
  function move(id: string, delta: number): void {
    if (delta === 0) {
      return;
    }
    const slice = [...items.value];
    const from = slice.findIndex((item) => item.id === id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= slice.length) {
      return;
    }
    const [moved] = slice.splice(from, 1);
    slice.splice(to, 0, moved);
    parent.value = commit(slice);
  }

  /** Replaces the whole slice with a caller-supplied reordered version (drag-and-drop). */
  function reorder(nextSlice: T[]): void {
    parent.value = commit(nextSlice);
  }

  return { items, remove, move, reorder };
}
